/**
 * The reader's IANA time zone, and the header it travels in.
 *
 * Shared web+mobile code on purpose. The backoffice runs ONE sanitiser over
 * both clients, so the header name and the value shape have to agree, and the
 * only way to guarantee that is for there to be a single implementation. The
 * neighbouring `mobile/src/lib/client-headers.ts` mirrors its patterns from the
 * backend by hand and says so in a comment that ends "there is no build-time
 * check that keeps them so" — that is the weakness this file exists to avoid
 * repeating between our own two clients.
 *
 * `Intl.DateTimeFormat().resolvedOptions().timeZone` is the source on both
 * platforms: present in Hermes and in every browser the web app supports.
 */

/** The wire name. Both clients import this rather than typing the string. */
export const TIMEZONE_HEADER = 'X-Timezone';

/**
 * Mirrors the client-metadata cap the mobile headers already use. A zone name
 * longer than this is not a zone name.
 */
const MAX_TIMEZONE_LENGTH = 64;

/**
 * IANA zone names: `Area/Location`, occasionally with a third part
 * (`America/Argentina/Buenos_Aires`), plus the bare regional names like `UTC`
 * and `GMT`. Letters, digits, underscore, plus and minus are the character set
 * in the database — `Etc/GMT+10` and `America/Port-au-Prince` are both real.
 *
 * An allowlist rather than a denylist, for the reason `client-headers.ts` gives
 * at length: React Native's `whatwg-fetch` does no validation, so whatever we
 * set reaches OkHttp, which throws on any byte outside ASCII 0x20-0x7e and
 * takes the customer's whole account surface down with it. A pattern that can
 * only match ASCII makes that unrepresentable rather than unlikely.
 */
const TIMEZONE_PATTERN = /^[A-Za-z][A-Za-z0-9_+-]*(?:\/[A-Za-z0-9_+-]+){0,2}$/;

/**
 * The reader's zone, or `undefined` when we do not have one to send.
 *
 * `undefined` means OMIT THE HEADER. It must never be coerced into a string,
 * and there is deliberately no fallback: a client-invented `"UTC"` is a wrong
 * answer wearing the costume of a real one, and the backoffice would show it on
 * a customer profile with no way to tell it apart from a reader who genuinely
 * is in UTC. An absent header is a clean absence the backend already knows how
 * to leave alone.
 *
 * A zone that genuinely RESOLVES to `UTC` is a real value and is sent. The rule
 * is against inventing one, not against the string.
 *
 * `Intl` is wrapped because it is not guaranteed: a JS engine built without ICU
 * data throws on `resolvedOptions()`, and the older React Native Android builds
 * that shipped a trimmed ICU returned an empty string instead. Both are handled
 * the same way, by sending nothing.
 */
export function resolveTimeZone(): string | undefined {
  let zone: unknown;
  try {
    zone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    // No ICU data, or an engine without a usable Intl. Nothing to report.
    return undefined;
  }

  if (typeof zone !== 'string') return undefined;
  const trimmed = zone.trim();
  if (!trimmed) return undefined;
  if (trimmed.length > MAX_TIMEZONE_LENGTH) return undefined;
  if (!TIMEZONE_PATTERN.test(trimmed)) return undefined;
  return trimmed;
}
